import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const VARIANTS = [
  {
    id: "a",
    name: "Тёмное золото",
    desc: "Глубокий графит и золото 999.9. Ночной премиум, текущая версия сайта.",
    bg: "#0A0B0E",
    accent: "#D4AF37",
    card: "#1A1D29",
    text: "#F8FAFC",
  },
  {
    id: "b",
    name: "Светлый изумруд",
    desc: "Слоновая кость, глубокий изумруд и латунь. Светлая редакционная подача, арочное фото.",
    bg: "#F5F2EA",
    accent: "#0E5C43",
    card: "#FFFFFF",
    text: "#14201B",
  },
];

const Preview = ({ v }) => (
  <div className="h-32 w-full overflow-hidden rounded-2xl border border-white/10 p-4" style={{ backgroundColor: v.bg }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: v.accent }} />
        <span className="h-2 w-16 rounded-full" style={{ backgroundColor: v.text, opacity: 0.7 }} />
      </div>
      <span className="h-5 w-14 rounded-full" style={{ backgroundColor: v.accent }} />
    </div>
    <div className="mt-5 space-y-2">
      <span className="block h-3 w-3/4 rounded-full" style={{ backgroundColor: v.text, opacity: 0.85 }} />
      <span className="block h-3 w-1/2 rounded-full" style={{ backgroundColor: v.text, opacity: 0.5 }} />
    </div>
    <div className="mt-4 flex gap-2">
      <span className="h-7 w-20 rounded-xl" style={{ backgroundColor: v.accent }} />
      <span className="h-7 w-20 rounded-xl border" style={{ borderColor: `${v.text}33`, backgroundColor: v.card }} />
    </div>
  </div>
);

export const Landings = ({ api, demo }) => {
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (demo) {
      setActive("a");
      return;
    }
    api
      .get("/settings")
      .then((r) => setActive(r.data.active_landing || "a"))
      .catch(() => setActive("a"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (id) => {
    if (id === active) return;
    const prev = active;
    setActive(id);
    if (demo) {
      toast.success("Шаблон: выбор не сохраняется в демо-режиме");
      return;
    }
    setSaving(id);
    try {
      await api.put("/settings", { active_landing: id });
      const cached = JSON.parse(localStorage.getItem("ap_settings") || "{}");
      cached.active_landing = id;
      localStorage.setItem("ap_settings", JSON.stringify(cached));
      toast.success("Лендинг переключён", { description: "На сайте применится сразу, без промежуточного экрана." });
    } catch {
      setActive(prev);
      toast.error("Не удалось сохранить выбор");
    } finally {
      setSaving(null);
    }
  };

  if (active === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-landings" className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-slate-50">Лендинги</h1>
        <p className="mt-2 max-w-xl text-sm text-slate-500">
          Две версии сайта с одинаковым текстом и функционалом. Выбор сохраняется в базу и применяется мгновенно —
          посетитель сразу видит нужный вариант, без переключения на глазах.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {VARIANTS.map((v) => {
          const isActive = active === v.id;
          return (
            <div
              key={v.id}
              data-testid={`landing-card-${v.id}`}
              className={`rounded-[2rem] border p-6 transition-all duration-300 sm:p-7 ${
                isActive ? "border-[#D4AF37]/50 bg-[#161925]" : "border-white/8 bg-[#12141C] hover:border-white/20"
              }`}
            >
              <Preview v={v} />
              <div className="mt-5 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-100">{v.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{v.desc}</p>
                </div>
                {isActive && (
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-semibold text-[#F3E5AB]">
                    <Check className="h-3 w-3" /> Активен
                  </span>
                )}
              </div>
              <button
                onClick={() => choose(v.id)}
                disabled={isActive || saving === v.id}
                data-testid={`landing-choose-${v.id}`}
                className={`mt-5 flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-semibold transition-all ${
                  isActive
                    ? "cursor-default bg-[#D4AF37]/15 text-[#F3E5AB]"
                    : "bg-[#D4AF37] text-[#0A0B0E] hover:shadow-[0_0_36px_rgba(212,175,55,0.3)] active:scale-[0.98]"
                } disabled:opacity-70`}
              >
                {saving === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isActive ? "Используется сейчас" : "Сделать активным"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
